import { tv, VariantProps } from 'tailwind-variants';

const fileCardVariants = tv({
  slots: {
    container: 'flex flex-col gap-4 max-w-full animate-journey-slide-up',
    card: 'group flex items-center gap-3 px-3 py-2.5 bg-[#fbfaf9] outline-none border border-solid border-gray-200 rounded-lg transition-all duration-200 max-w-full',
    fileIcon: 'w-5 h-5 flex-shrink-0 transition-transform duration-200',
    fileInfo: 'flex flex-col items-start flex-1 min-w-0 overflow-hidden',
    fileName: 'text-sm text-gray-900 truncate w-full',
    detail: 'text-xs text-gray-500 font-medium text-left truncate w-full',
    actionIcon: 'w-4 h-4 text-gray-500 flex-shrink-0 transition-colors duration-200 group-hover:text-gray-900',
    spinner: 'animate-spin h-4 w-4 border-2 border-gray-400 border-t-blue-600 rounded-full flex-shrink-0',
    errorContainer: 'flex items-center gap-1.5 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-md',
    errorIcon: 'w-3.5 h-3.5 flex-shrink-0',
  },
  variants: {
    fileType: {
      excel: { fileIcon: 'text-emerald-600' },
      csv: { fileIcon: 'text-green-600' },
      pdf: { fileIcon: 'text-rose-600' },
      image: { fileIcon: 'text-sky-600' },
      text: { fileIcon: 'text-slate-600' },
      document: { fileIcon: 'text-blue-600' },
      presentation: { fileIcon: 'text-amber-600' },
      json: { fileIcon: 'text-violet-600' },
      default: { fileIcon: 'text-gray-600' },
    },
    // Only a card that is one button (download) gets hover and focus feedback.
    interactive: {
      true: {
        card: 'hover:bg-[#f4f1ed] hover:border-gray-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/20',
        fileIcon: 'group-hover:scale-110',
        fileName: 'group-hover:font-semibold',
      },
    },
  },
  compoundVariants: [
    { interactive: true, fileType: 'excel', class: { card: 'hover:bg-emerald-50/50' } },
    { interactive: true, fileType: 'csv', class: { card: 'hover:bg-green-50/50' } },
    { interactive: true, fileType: 'pdf', class: { card: 'hover:bg-rose-50/50' } },
    { interactive: true, fileType: 'image', class: { card: 'hover:bg-sky-50/50' } },
    { interactive: true, fileType: 'text', class: { card: 'hover:bg-slate-50/50' } },
    { interactive: true, fileType: 'document', class: { card: 'hover:bg-blue-50/50' } },
    { interactive: true, fileType: 'presentation', class: { card: 'hover:bg-amber-50/50' } },
    { interactive: true, fileType: 'json', class: { card: 'hover:bg-violet-50/50' } },
    { interactive: true, fileType: 'default', class: { card: 'hover:bg-gray-50' } },
  ],
  defaultVariants: {
    fileType: 'default',
    interactive: false,
  },
});

type FileTypeVariant = VariantProps<typeof fileCardVariants>['fileType'];

const fileTypeVariant = (mimeType: string): FileTypeVariant => {
  switch (mimeType) {
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel':
      return 'excel';
    case 'text/csv':
      return 'csv';
    case 'application/pdf':
      return 'pdf';
    case 'image/png':
    case 'image/jpeg':
      return 'image';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/msword':
      return 'document';
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    case 'application/vnd.ms-powerpoint':
      return 'presentation';
    case 'application/json':
      return 'json';
    case 'text/plain':
      return 'text';
    default:
      return 'default';
  }
};

export { fileCardVariants, fileTypeVariant };
